import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { useCart } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_IMG_SINGLE = "https://images.unsplash.com/photo-1517409091671-180985f2ca15?auto=format&fit=crop&w=1600&q=80";
const PRODUCT_IMG_BUNDLE = "https://images.unsplash.com/photo-1453090927415-5f45085b65c0?auto=format&fit=crop&w=1600&q=80";

export default function ProductPage() {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [selectedSku, setSelectedSku] = useState("aura-bundle-3");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Could not load products"));
  }, []);

  const selected = products.find((p) => p.id === selectedSku);
  const heroImg =
    selectedSku === "aura-bundle-3" ? PRODUCT_IMG_BUNDLE : PRODUCT_IMG_SINGLE;

  const handleAdd = () => {
    if (!selected) return;
    addItem(selectedSku, qty);
    toast.success(`Added ${qty} × ${selected.name} to cart`);
  };

  return (
    <div data-testid="product-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Image */}
          <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
            <img
              src={heroImg}
              alt="Aura Sleeve product"
              className="w-full h-full object-cover transition-opacity duration-500"
              data-testid="product-hero-image"
            />
            <div className="absolute top-6 left-6 px-3 py-1 bg-black/70 backdrop-blur-md border border-white/30">
              <p className="text-xs uppercase tracking-[0.2em] text-white">Aura Sleeve</p>
            </div>
          </div>

          {/* Info */}
          <div>
            <p className="eyebrow mb-4">Stageletics</p>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-none">
              Aura Sleeve
            </h1>
            <p className="mt-4 text-lg text-zinc-400 leading-relaxed">
              A premium cord cover designed for microphone stands — built to
              hide ME-1 monitor cables, XLR runs, and IEM lines behind a clean
              matte finish.
            </p>

            {/* Option Selector */}
            <div className="mt-10">
              <p className="eyebrow mb-4">Select option</p>
              <div className="space-y-3">
                {products.map((p) => {
                  const active = selectedSku === p.id;
                  const isBundle = p.id === "aura-bundle-3";
                  return (
                    <button
                      key={p.id}
                      data-testid={`product-option-${p.id}`}
                      onClick={() => setSelectedSku(p.id)}
                      className={`w-full text-left border p-5 flex items-center justify-between transition-all ${
                        active
                          ? "border-[#E8A851] bg-gradient-to-br from-[#E8A851]/10 to-transparent"
                          : "border-white/10 hover:border-white/30 bg-white/[0.02]"
                      }`}
                    >
                      <div>
                        <p
                          className={`text-sm uppercase tracking-[0.15em] mb-1 ${
                            active ? "text-[#E8A851]" : "text-zinc-400"
                          }`}
                        >
                          {isBundle ? "Bundle of 3 · Best Value" : "Single"}
                        </p>
                        <p className="font-display text-xl font-medium">
                          {p.name}
                        </p>
                      </div>
                      <p className="font-display text-2xl font-semibold">
                        ${p.unit_price.toFixed(2)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Qty */}
            <div className="mt-8 flex items-center gap-6">
              <p className="eyebrow">Quantity</p>
              <div className="flex items-center border border-white/20">
                <button
                  data-testid="qty-decrement"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="p-3 hover:bg-white/5"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span
                  data-testid="qty-display"
                  className="px-6 font-display text-lg"
                >
                  {qty}
                </span>
                <button
                  data-testid="qty-increment"
                  onClick={() => setQty((q) => Math.min(50, q + 1))}
                  className="p-3 hover:bg-white/5"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="add-to-cart-button"
                onClick={handleAdd}
                className="btn-primary flex-1 justify-center"
              >
                <ShoppingBag className="w-4 h-4" />
                Add to Cart
              </button>
              <Link
                to="/cart"
                data-testid="go-to-cart-button"
                className="btn-secondary justify-center"
              >
                View Cart
              </Link>
            </div>

            {/* Trust */}
            <div className="mt-10 grid grid-cols-3 gap-3 border-t border-white/10 pt-8">
              <div className="flex flex-col gap-2">
                <Truck className="w-5 h-5 text-[#E8A851]" />
                <p className="text-xs text-zinc-400 leading-snug">
                  Flat $7.99 US shipping
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <RotateCcw className="w-5 h-5 text-[#E8A851]" />
                <p className="text-xs text-zinc-400 leading-snug">
                  30-day returns
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <ShieldCheck className="w-5 h-5 text-[#E8A851]" />
                <p className="text-xs text-zinc-400 leading-snug">
                  Secure Stripe checkout
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Specs */}
        <div className="mt-32 grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-white/10 pt-16">
          <div>
            <p className="eyebrow mb-6">What's inside</p>
            <h2 className="font-display text-3xl font-medium tracking-tight mb-6">
              Engineered for the stage.
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              The Aura Sleeve is built from a durable matte-black material
              engineered for the stage. It's rigid yet lightweight,
              dimensionally precise, won't fade or fray, and the textured
              matte finish stays non-reflective under stage lighting. Made
              in-house, one at a time.
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              ["Material", "Matte-black composite"],
              ["Color", "Stage black"],
              ["Length", "Adjustable, 24–48 in"],
              ["Compatibility", "Most mic stands"],
              ["Install", "No tools, few minutes"],
              ["Warranty", "1-year material"],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#050505] p-6">
                <dt className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  {k}
                </dt>
                <dd className="text-base text-white">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Wholesale CTA */}
        <div className="mt-24 border border-white/10 bg-gradient-to-br from-[#E8A851]/10 via-white/[0.02] to-transparent p-10 md:p-16 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="max-w-xl">
            <p className="eyebrow mb-3 text-[#E8A851]">Ordering 10 or more?</p>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight">
              Outfit every stand on every campus.
            </h2>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              Volume pricing, single-invoice multisite shipping, and PO terms
              for churches, schools, and AV integrators.
            </p>
          </div>
          <Link
            to="/wholesale"
            data-testid="product-wholesale-cta"
            className="btn-primary shrink-0"
          >
            Request a quote
          </Link>
        </div>
      </div>
    </div>
  );
}
